import React, { DragEvent, Dispatch, SetStateAction } from "react";

interface UseDragAndDropListProps<T> {
  items: T[];
  setItems: Dispatch<SetStateAction<T[]>> | Function;
}

/**
 * Hook that will give you the ability to reorder a list of items.
 * See an easy usage example in tests.
 */
export function useDragAndDropList<T>({
  items,
  setItems,
}: UseDragAndDropListProps<T>) {
  const [draggedItem, setDraggedItem] = React.useState<T | null>(null);

  const onDragStart = (index: number) => {
    return (e: DragEvent<HTMLElement>) => {
      const parentElement = e.currentTarget.parentElement;

      // istanbul ignore next - not worth testing
      // almost impossible to not exist a parent node.
      if (parentElement === null) {
        return;
      }

      setDraggedItem(items[index]);

      e.dataTransfer.setDragImage(parentElement, 20, 20);
      e.dataTransfer.effectAllowed = "move";
    };
  };

  const onDragOver = (index: number) => {
    return (e: DragEvent<HTMLElement>) => {
      e.preventDefault();

      if (draggedItem === null) {
        return;
      }

      const draggedOverItem = items[index];

      // if the item is dragged over itself, ignore
      if (draggedItem === draggedOverItem) {
        return;
      }

      // filter out the currently dragged item
      const filteredItems = items.filter((item) => item !== draggedItem);

      // add the dragged item after the dragged over item
      filteredItems.splice(index, 0, draggedItem);

      setItems(filteredItems);
    };
  };

  const onDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDraggedItem(null);
  };

  return {
    /**
     * Give it to your item containing the draggable element
     * onDragOver={onDragOver(index)}
     */
    onDragOver,
    /**
     * Give it to your element with 'draggable={true}' prop
     * onDragStart={onDragStart(index)}
     */
    onDragStart,
    /**
     * Give it to your element with 'draggable={true}' prop
     * onDrop={onDrop}
     */
    onDrop,
  };
}
